import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export interface SuggestionsLoadingProps {
  /** Skeleton rows — the height the results are expected to take. */
  rows: number;
}

/**
 * Skeleton rows with the `SuggestionRow` geometry, so results replace them
 * without a jump. Fades in after a short delay — fast answers show nothing.
 */
export const SuggestionsLoading: React.FC<SuggestionsLoadingProps> = ({ rows }) => (
  <div className={styles.loading} data-testid="header-search-loading" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className={styles.skeletonRow}>
        <span className={styles.skeletonLogo} />
        <span className={styles.skeletonText}>
          <span className={styles.skeletonTitle} />
          <span className={styles.skeletonSubtitle} />
        </span>
        <span className={styles.skeletonBadge} />
      </div>
    ))}
  </div>
);

export interface SuggestionsCaptionProps {
  /** Tab name. */
  scope: string;
  /** Tournaments in the tab with its filters. */
  count: number;
}

/** Muted "<Tab> · <count>" line above the tab list (empty field). */
export const SuggestionsCaption: React.FC<SuggestionsCaptionProps> = ({ scope, count }) => {
  const { t } = useTranslation('common');

  return (
    <p className={styles.caption} data-testid="header-search-scope-caption">
      {t('headerSearch.scopeCaption', { scope, count })}
    </p>
  );
};

/** Neutral line shown when the search request failed. */
export const SuggestionsError: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <div className={styles.empty} role="status" data-testid="header-search-error">
      <p className={styles.mutedText}>{t('headerSearch.error')}</p>
    </div>
  );
};

export interface SuggestionsShowAllProps {
  onClick: () => void;
  /** Total matches — shown in the label when given. */
  count?: number;
  /** Placement (margins) from the parent. */
  className?: string;
}

/** "Show all results" link-style button (submits the query). */
export const SuggestionsShowAll: React.FC<SuggestionsShowAllProps> = ({ onClick, count, className }) => {
  const { t } = useTranslation('common');

  return (
    <button
      type="button"
      className={[styles.showAll, className].filter(Boolean).join(' ')}
      onClick={onClick}
      data-testid="header-search-show-all"
    >
      {count === undefined ? t('headerSearch.showAll') : t('headerSearch.showAllCount', { count })}
    </button>
  );
};

export interface SuggestionsEmptyProps {
  /** Adds "Show all results" (submits the query). */
  onShowAll?: () => void;
}

/** "Nothing found", optionally with "Show all results". */
export const SuggestionsEmpty: React.FC<SuggestionsEmptyProps> = ({ onShowAll }) => {
  const { t } = useTranslation('common');

  return (
    <div className={styles.empty} data-testid="header-search-empty">
      <p className={styles.emptyTitle}>{t('headerSearch.nothingFound')}</p>
      {onShowAll && <SuggestionsShowAll onClick={onShowAll} />}
    </div>
  );
};
